<script setup>
import { computed } from "vue";
import { useStore } from "vuex";
import { useTyping } from "@/composables/useTyping";
import MainButton from "../../inputs/MainButton.vue";
import Tabs from "../../tabs/Tabs.vue";

const store = useStore();
// While a text box has focus the keyboard is up, so the bar gets out of the way
const { typing } = useTyping();

const emit = defineEmits(["newPrompt"]);

const openPurpose = computed(() => store.state.drawer.openPurpose);
const tabList = computed(() => store.state.tabs.tabList);
const currentTab = computed(() => store.state.tabs.currentTab);
const showResult = computed(() => store.state.result.showResult);
// Filling in blanks has its own card at the bottom
const fillingBlank = computed(() => Boolean(store.state.result.fillingBlank));
const loadingResult = computed(() => store.state.result.loading);
const newInput = computed(() => store.state.newInput);
const questionsReady = computed(
  () => store.getters["questions/questionsReady"]
);

// A make already written (or being written) stays one tap away while the answers
// and style are changed
const hasMake = computed(
  () =>
    loadingResult.value ||
    Boolean(store.state.result.generatedText || store.state.result.image)
);
const showMake = () => store.dispatch("result/showMake");

// Edit sits with Share and Copy above the make, so this bar only makes it again
const getResult = (regenerate) =>
  store.dispatch("result/getResult", { regenerate });
const setTab = (tab) => store.dispatch("tabs/setTab", tab);
</script>

<template>
  <transition
    enter-active-class="transition duration-200 ease-out"
    enter-from-class="translate-y-full"
    leave-active-class="transition duration-150 ease-in"
    leave-to-class="translate-y-full"
  >
    <section
      v-if="openPurpose === 'createMake' && !typing && !fillingBlank"
      class="fixed bottom-0 z-25 flex-col w-full bg-white lg:hidden flex shadow-[0_-4px_16px_rgba(35,31,66,0.06)]"
    >
      <Tabs
        class="w-full pb-2 pt-2 sm:justify-center"
        v-if="!showResult"
        ref="tabs"
        :tab-list="tabList"
        :current-tab="currentTab"
        @change="setTab"
      />
      <MainButton
        class="w-full"
        setPadding="px-6 py-3"
        :curve="false"
        @clicked="emit('newPrompt')"
        v-if="newInput"
      >
        <label class="uppercase cursor-pointer">re-do</label>
        <font-awesome-icon
          class="ml-2 cursor-pointer"
          :icon="['fas', 'rotate-right']"
        />
      </MainButton>
      <div
        v-else-if="showResult ? !loadingResult : questionsReady || hasMake"
        class="w-full flex"
      >
        <button
          v-if="!showResult && hasMake"
          id="shepherd-target-back-to-make"
          type="button"
          class="flex shrink-0 items-center justify-center gap-2 bg-primary px-5 py-3 text-base font-bold text-white transition-colors hover:bg-primary/90"
          :class="{ 'flex-1': !questionsReady }"
          @click="showMake"
        >
          <font-awesome-icon :icon="['fas', 'file-lines']" />
          Your make
        </button>
        <MainButton
          v-if="showResult || questionsReady"
          id="shepherd-target-make-redo-mobile"
          setPadding="px-6 py-3"
          class="flex-1"
          :curve="false"
          @clicked="getResult(showResult)"
        >
          <label class="uppercase cursor-pointer">
            {{ showResult ? "re-generate" : "generate" }}
          </label>
          <font-awesome-icon
            class="ml-2 cursor-pointer"
            :icon="
              showResult ? ['fas', 'rotate-right'] : ['fas', 'chevron-right']
            "
          />
        </MainButton>
      </div>
    </section>
  </transition>
</template>
